import { useState, useEffect, useCallback, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { useTwilioToken } from './useTwilioToken';
import { supabase } from '@/integrations/supabase/client';

export type SoftphoneStatus = 'initializing' | 'ready' | 'offline' | 'busy' | 'error';
export type CallDirection = 'inbound' | 'outbound';

export interface ActiveCallInfo {
  call: Call;
  direction: CallDirection;
  remoteNumber: string;
  startedAt: Date;
  contactId?: string;
  leadId?: string;
  accountId?: string;
}

export interface IncomingCallInfo {
  call: Call;
  from: string;
}

export function useTwilioDevice() {
  const [status, setStatus] = useState<SoftphoneStatus>('initializing');
  const [activeCall, setActiveCall] = useState<ActiveCallInfo | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [networkWarnings, setNetworkWarnings] = useState<string[]>([]);

  const deviceRef = useRef<Device | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: tokenData, refetch: refetchToken, isError: tokenError } = useTwilioToken();

  // Initialize and manage Device lifecycle
  useEffect(() => {
    if (tokenError) {
      setStatus('offline');
      return;
    }
    if (!tokenData?.token) {
      setStatus('initializing');
      return;
    }

    const device = new Device(tokenData.token, {
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      closeProtection: true,
      enableImplicitConstraints: true,
    });

    device.on('registered', () => setStatus('ready'));
    device.on('unregistered', () => setStatus('offline'));
    device.on('error', (err) => {
      console.error('Twilio Device error:', err);
      setStatus('error');
    });

    device.on('incoming', (call: Call) => {
      const from = call.parameters?.From || 'Unknown';
      setIncomingCall({ call, from });
    });

    device.on('tokenWillExpire', async () => {
      const { data } = await refetchToken();
      if (data?.token) {
        device.updateToken(data.token);
      }
    });

    device.register();
    deviceRef.current = device;

    return () => {
      if (device.state === Device.State.Registered) {
        device.unregister();
      }
      device.destroy();
      deviceRef.current = null;
    };
  }, [tokenData?.token, tokenError, refetchToken]);

  // Call duration timer
  useEffect(() => {
    if (activeCall) {
      setCallDuration(0);
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setCallDuration(0);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeCall]);

  const setupCallHandlers = useCallback((call: Call, direction: CallDirection, number: string, meta?: { contactId?: string; leadId?: string; accountId?: string }) => {
    setActiveCall({
      call,
      direction,
      remoteNumber: number,
      startedAt: new Date(),
      ...meta,
    });
    setStatus('busy');
    setIsMuted(false);
    setNetworkWarnings([]);

    call.on('disconnect', () => {
      setActiveCall(null);
      setStatus('ready');
      setNetworkWarnings([]);
    });

    call.on('cancel', () => {
      setActiveCall(null);
      setStatus('ready');
    });

    call.on('warning', (warningName: string) => {
      setNetworkWarnings(prev => [...new Set([...prev, warningName])]);
    });

    call.on('warning-cleared', (warningName: string) => {
      setNetworkWarnings(prev => prev.filter(w => w !== warningName));
    });
  }, []);

  const makeCall = useCallback(async (
    number: string,
    meta?: { contactId?: string; leadId?: string; accountId?: string; callerIdNumberId?: string }
  ) => {
    const device = deviceRef.current;
    if (!device || status !== 'ready') return;

    try {
      const connectParams: Record<string, string> = { To: number };
      if (meta?.callerIdNumberId) {
        connectParams.CallerId = meta.callerIdNumberId;
      }
      const call = await device.connect({ params: connectParams });
      setupCallHandlers(call, 'outbound', number, meta);
    } catch (err) {
      console.error('Failed to make call:', err);
    }
  }, [status, setupCallHandlers]);

  const hangUp = useCallback(() => {
    activeCall?.call.disconnect();
    setActiveCall(null);
    setStatus('ready');
  }, [activeCall]);

  const toggleMute = useCallback(() => {
    if (!activeCall) return;
    const newMuted = !isMuted;
    activeCall.call.mute(newMuted);
    setIsMuted(newMuted);
  }, [activeCall, isMuted]);

  const sendDigits = useCallback((digits: string) => {
    activeCall?.call.sendDigits(digits);
  }, [activeCall]);

  const acceptIncoming = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.call.accept();
    setupCallHandlers(incomingCall.call, 'inbound', incomingCall.from);
    setIncomingCall(null);
  }, [incomingCall, setupCallHandlers]);

  const rejectIncoming = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.call.reject();
    setIncomingCall(null);
  }, [incomingCall]);

  return {
    status,
    activeCall,
    incomingCall,
    isMuted,
    callDuration,
    networkWarnings,
    makeCall,
    hangUp,
    toggleMute,
    sendDigits,
    acceptIncoming,
    rejectIncoming,
  };
}
